import { ColorTag, Orientation, RationalNumber, CullingTag } from "@/utils/exif"
import type { GenericWhiteBalanceInfo } from "@/io/formats/raw/white-balance/generic-white-balance"
import type {
	BiasType,
	FeatureDistances,
	ImageFeature,
	PartialAdjustmentsMap,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { Adjustments } from "@/core/types"
import { PresetItem } from "@/store/presets/presets-service"
import { Rect } from "../ai/detection/face-detection"
import { EyeStatus } from "../ai/detection/face-scoring"
import { LuminanceScoreStatistics } from "@/lib/ai/calibration/ai-calibration-brightness"
import { ColorMatchImageItem } from "@/store/colorMatch/color-match-service"
import { SceneDetectionResult } from "../ai/detection/scene-detection"
import { ReferenceColorMatchFeatures } from "../ai/color-match/utils"
import { Diff } from "deep-diff"
import { WhiteBalance } from "@/core/photo"
import { StyleVisibility } from "@/api/dto/styles"

// Culling threshold enums - defined here to avoid circular dependencies with culling-state.ts
// NOTE: These numeric values are used as KEYS in the database (clusterLeaderIdMap)
// Do NOT change these values as it would break backward compatibility
// To change the threshold, see ../ai/ai-cluster.ts: ClusterThresholdValues
export enum ClusterThreshold {
	Loose = 0.22, // Storage key - do not change
	Similar = 0.16, // Storage key - do not change
	Exact = 0.12, // Storage key - do not change
	EditClustering = 0.235, // Storage key for edit mode clustering
	VerySimilar = 0.09, // Storage key for very similar neighbor in edit mode
	EditClusteringFinal = 1, // Special storage key for final edit mode clustering that should not be modified by mergeClusters
}

// SYNC: mirrored in src/utils/culling-eval-metrics.ts for headless eval scripts
export enum BlurryRejectThreshold {
	Lenient = 1.5,
	Balanced = 2.3,
	Strict = 3.2,
}

// SYNC: mirrored in src/utils/culling-eval-metrics.ts for headless eval scripts
export enum FaceRejectThreshold {
	Lenient = 1.5,
	Balanced = 2.3,
	Strict = 3.2,
}

export const SortOptions = [
	"filenameAscending",
	"filenameDescending",
	"dateAscending",
	"dateDescending",
	"modifiedDateAscending",
	"modifiedDateDescending",
	"customReordered",
]

export type SortOption = (typeof SortOptions)[number]

export type PhotoTag = Rating | PhotoFlagStatus | ColorTag | CullingTag

export const RatingValues = [0, 1, 2, 3, 4, 5] as const
export type Rating = (typeof RatingValues)[number]

export type PhotoProcessingStatus = "none" | "pending" | "applying" | "error"

export type PhotoKind = "default" | "reference" | "locked"

export const PhotoFlagValues = ["flagged", "rejected", "unflagged"] as const
export type PhotoFlagStatus = (typeof PhotoFlagValues)[number]

export type Visibility = "hidden" | "visible"

export type PhotosViewState = "grid" | "focus" | "before/after"

export type DisplayGroup =
	| "all"
	| "references"
	| "locked"
	| "processed"
	| "processing"
	| "suggested"
	| "unsupported"
	| "keepers"
	| "candidates"
	| "rejects"
	| "cullingProcessing"

export type TaskRecord =
	| PresetTaskRecord
	| StyleTaskRecord
	| AutoAdjustmentsTaskRecord
	| BasicAutoAdjustmentsTaskRecord
	| MarkReferenceTaskRecord
	| ColorMatchTaskRecord
	| DetectMaskTaskRecord

export interface PresetTaskRecord {
	type: "apply-preset"
	preset: PresetItem
	strength: number
	allowStrengthAdjustment: boolean
}

export interface AutoAdjustmentsTaskRecord {
	type: "apply-auto"
}

export interface BasicAutoAdjustmentsTaskRecord {
	type: "apply-basic-auto"
}

export interface DetectMaskTaskRecord {
	type: "detect-mask"
	maskId: string
	maskType: string
}

export interface ColorMatchTaskRecord {
	type: "apply-color-match"
	referenceImage: ColorMatchImageItem
	useParameters: boolean
	strength: number
	skinToneOptimized: boolean
	asShotWhiteBalance: WhiteBalance
}

export interface StyleTaskRecord {
	type: "apply-style"
	styleId: string
}

export interface MarkReferenceTaskRecord {
	type: "mark-reference"
}

export interface PhotoRecord {
	id: string
	parentId: string | null
	fileName: string

	//Editing
	historyIndex: number | null
	adjustmentsDigest: string | null
	presetId: string | null
	styleId: string | null
	styleVersion: number | null
	status: PhotoProcessingStatus
	isAnchorImage: boolean
	currentTask: TaskRecord | null
	kind: PhotoKind

	//Culling
	// isSuggestedImage: boolean //deprecated, use smartCullingTag
	smartCullingTag: CullingTag | null
	manualCullingTag: CullingTag | null // to record user overridden culling tag
	rating: Rating | null
	colorTag: ColorTag | null
	flag: PhotoFlagStatus | null
	isHidden: boolean

	//Other
	customSortingIndex: number | null
	isUnsupported: boolean
	error: Error | null
	adjustedDate: number | null // UTC
	publishedDates: Record<string, number> | null // map[galleryId] = date
	publishedExportSettings?: Record<string, string> | null // map[galleryId] = settings key
}

export interface ImageInfoRecord {
	imageId: string
	features: ImageFeature
	aiAdjustmentsMap: PartialAdjustmentsMap // map[presetId] = aiAdjustments to store auto adjustments for each preset
	presetAdjustmentsMap: PartialAdjustmentsMap | null // map[presetId] = presetAdjustments to store presetAdjustments for real or derived preset

	// Reset Start. Yuhe: if support multiple styles, all the following properties need to be reset to null when switching style
	keyNeighborId: string | null // the representative neighbor in a cluster: first processed neighbor or nearest approved neighbor

	aiStyleAdjustments: Partial<Adjustments> | null // adjustments applied by ai style
	presetId: string | null // This stores the presetId that should be used according to current ai-style, could be different from the actual presetId applied (by user or previous style version)
	biases: Partial<BiasType> | null // userAdjustments - aiAdjustments or aiStyleAdjustments - aiAdjustments

	// store the K nearest references with distance, and update it when new reference photos are added
	nearestReferences: [string, FeatureDistances][] | null // [imageId, distances] where distances is [sceneDist, lightDist, wbDist, metaDist, overallDist]
	// needApplyPreset: boolean | 0 | 1 | null
	// needApplyBias: boolean | 0 | 1 | null
	needApplyStyle: boolean | 0 | 1 | null // ai style update is available but not applied yet

	colorMatchReference?: ReferenceColorMatchFeatures | null
	referenceWebpData256x256Url?: string | null
	referenceWebpData256x256?: Uint8Array | null
	styleBiases?: Partial<Adjustments> | null // overall style biases set by style tuning dialog
	// Reset end.

	sceneClusterFeature?: Float32Array | null
	faceColors?: Float32Array | null
	faceWeights?: Float32Array | null
}

export enum RatingInCluster {
	undesirablePick = 0,
	unlikelyPick = 1,
	potentialPick = 2,
}

export interface FaceCullingInfo {
	faceRect: Rect // padded, square rects
	unPaddedRect?: Rect // unpadded, nonsquare rect used to estimate focus/eye scores
	confidence?: number // float 0-1, confidence score for face detection
	eyeScore?: EyeStatus // deprecated, use eyeStatus instead
	eyeStatus?: EyeStatus
	eyeEstScore?: number
	focusScore?: number // float range from 0 to 10
	expressionScore?: number // float 0-5, internal use, combined model-predicted eye and expression scores
}

export interface CullingScoreItem {
	// These scores typically range from 1 to 5
	face?: number
	lighting: number
	blur: number
	overallFloat: number
	overall: number
	rankScore?: number // internal use
	isKeeperMap?: Record<number, boolean> // map[count] = true: selected as keeper with the given target count; won't be unselected as long as target count keeps the same
}

export interface CullingInfoRecord {
	photoId: string
	faceInfo: FaceCullingInfo[]
	overallEyeScore?: number // float, combined overall eyeEstScore of all major faces, not really used
	overallFocusScore?: number // 1 - 10, deprecated, use scoreItem.blur instead

	// These 4 fields are only available after clustering
	clusterLeaderIdMap?: Partial<Record<ClusterThreshold, string>> // a cluster's first photo
	rankInCluster?: number // not used, might be used for future if we want to display rank in cluster
	clusterSize?: number // not used
	overallRating?: RatingInCluster // not used

	scoreItem?: CullingScoreItem

	deepClusterFeature?: Float32Array // old, deprecated; duplicate as imageInfo.features.deepCluster
	scene?: SceneDetectionResult // new feature for clustering; duplicate as imageInfo.features.scene
	sceneClusterFeature?: Float32Array // new column to store as BLOB, replace scene.clusterFeature

	luminanceScoreStats?: LuminanceScoreStatistics // internal use for scoring
}

interface PhotoAuxiliaryMetadata {
	whiteBalanceInfo?: GenericWhiteBalanceInfo
	hasComputedDimensions?: boolean
	[key: string]:
		| string
		| number
		| number[]
		| RationalNumber[]
		| boolean
		| GenericWhiteBalanceInfo
		| undefined
}

export interface PhotoMetadataRecord {
	photoId: string
	size: number
	extension: string
	isComplete: boolean
	hasEmbeddedPreview: boolean | null // null = unknown, true = has preview, false = no preview
	dateCreated: number | null // UTC
	dateCreatedOffset: number | null // UTC offset in minutes
	dateModified: number
	width: number | null
	height: number | null
	orientation: Orientation | null
	iso: number | null
	focalLength: number | null
	focalLength35mm: number | null
	focusDistance: number | null
	shutterSpeed: number | null
	fNumber: number | null
	lensMake: string | null
	lensModel: string | null
	cameraMake: string | null
	cameraModel: string | null
	cameraSerialNumber: string | null
	flash: number | null
	rating: Rating | null
	artist: string | null
	copyright: string | null
	colorTag: ColorTag | null
	flag: PhotoFlagStatus | null
	whiteLevel: number | null
	blackLevel: number | null
	auxiliaryData: PhotoAuxiliaryMetadata | null
}

export interface ObjectRecord {
	id: string
	data: Uint8Array
}

export enum HistoryRecordAction {
	Import,
	AutoEnhance,
	ApplyPreset,
	ApplyAIStyle,
	ManuallyAdjust,
	PasteAdjustments,
	ApplyColorMatch,
	Reset,
}

export type StoredAdjustmentsHistoryRecord = Omit<AdjustmentsHistoryRecord, "adjustments"> & {
	adjustmentsDelta: Diff<Adjustments>[] | undefined
}

// Record used in the application with resolved adjustments
export interface AdjustmentHistoryInfo {
	message: string
	presetId?: string
	changedValue?: string
	styleId?: string
	styleVersion?: number
	action: HistoryRecordAction
}

export interface AdjustmentsHistoryRecord extends AdjustmentHistoryInfo {
	adjustments: Adjustments
	photoId: string
	stepIndex: number
	adjustmentsDigest: string
}

export type AdjustmentsHistory = AdjustmentsHistoryRecord[]

//Stores in catalog, for per project persistence
export interface ProjectInfoRecord {
	id: string // UUID
	name: string
	styleId: string | null

	sortOption: SortOption | null
	needsSetup?: boolean | null
	isExpressSetup?: boolean | null
	isManual?: boolean | null
	isCulling?: boolean | null
	selectedTags?: PhotoTag[] | null
	suggestedKeeperCount?: number | null
	ratingFilters?: Rating[] | null
	pathFilters?: string[] | null
	colorFilters?: ColorTag[] | null
	cameraFilters?: string[] | null
	flagFilters?: PhotoFlagStatus[] | null
	fileFormatFilters?: string[] | null
	visibilityFilters?: Visibility[] | null
	displayGroup?: DisplayGroup | null
	publishCollectionId?: string | null
	publishGalleryId?: string | null
	cullingSettings?: ProjectCullingSettings | null
	currentPhotoId?: string | null
	photosViewState?: PhotosViewState | null
}

export interface ProjectCullingSettings {
	groupSimilarPhotos: boolean
	groupSimilarThreshold: ClusterThreshold
	sortByScoreWithinGroup: boolean

	suggestKeepers: boolean
	suggestedKeeperCount: number

	rejectBlurryPhotos: boolean
	blurryPhotoThreshold: BlurryRejectThreshold
	rejectPoorFaceQuality: boolean
	poorFaceQualityThreshold: FaceRejectThreshold
	rejectPoorExposure: boolean
	autoAdvanceAfterTagging: boolean
}

export const defaultCullingSettings: ProjectCullingSettings = {
	groupSimilarPhotos: true,
	groupSimilarThreshold: ClusterThreshold.Similar,
	sortByScoreWithinGroup: false,

	suggestKeepers: false,
	suggestedKeeperCount: 0,

	rejectBlurryPhotos: true,
	blurryPhotoThreshold: BlurryRejectThreshold.Balanced,
	rejectPoorFaceQuality: true,
	poorFaceQualityThreshold: FaceRejectThreshold.Balanced,
	rejectPoorExposure: true,
	autoAdvanceAfterTagging: false,
}

export interface ProjectLockRecord {
	sessionId: string // UUID
	lastSave: number
}

export type ProjectResetOption = "edits" | "ratings"

export interface StyleReferenceRecord {
	imageId: string // Encoded remote/catalog reference key. Use imageInfo.imageId for local photo lookup.
	styleId: string
	imageInfo: ImageInfoRecord
	referenceId?: string // BE generated UUID, only available after upload
	beforeImageUrl?: string //Optional, only available for edited references
	afterImageUrl?: string //Optional, only available for published references downloaded from remote
	beforeImageData?: Blob //Local only
	afterImageData?: Blob //local Only
	isImplicit: boolean
}

export interface StyleAuthorRecord {
	name: string
	id?: string
	profileURL?: string
}

export interface StyleInfoRecord {
	styleId: string // BE generated UUID
	name: string // max length 128 char
	description?: string // max length 512 char
	author: StyleAuthorRecord
	categories?: string[]
	createdAt: Date
	updatedAt: Date
	referenceCount: number
	schemaVersion: number
	presets: Record<string, Partial<Adjustments>>
	lutUrls?: Record<string, string>
	coverImageUrl?: string
	visibility: StyleVisibility
	version: number

	prioritizeImageIds?: Array<string> // Local only, Process these photos first when applying style
	globalBiases?: Partial<Adjustments> // Local only, Overall biases for style, used for quick preview and can be updated by style tuning dialog
}
